import { Component, ContentChild, Input, OnInit, Optional } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../i18n/language.service';
import { BoxComponent } from '../../layout/box/box.component';
import { ModalDialogWrapperComponent } from '../../modal-dialog/modal-dialog-wrapper.component';
import { LoggingService } from '../../utils/logging.service';
import { I18N } from '../form.i18n';
import { BoxDirective } from './box.directive';

/**
 * Komponente zur Darstellung eines Formulares.
 *
 * @author imam
 * @author thm
 *
 */
@Component({
    selector: 'common-form',
    templateUrl: 'form.component.html'
})
export class FormComponent implements OnInit {

    /**
     * Der Titel des Formulars. Wird ignoriert, wenn sich das Formular in einem modalen Dialog befindet,
     * denn dieser hat selbst einen Titel.
     */
    @Input() headerTitle: string;

    /**
     * Der Text für eine Info-Box, die neben der Formular-Box angezeigt werden soll.
     * Wird ignoriert, wenn das Formular keine eigene Box darstellt
     * (weil es in einem modalen Dialog ist oder in eine Box eingebettet ist.)
     */
    @Input() infoBoxText: string;

    /**
     * Der Titel für eine Info-Box, die neben der Formular-Box angezeigt werden soll.
     * Wird ignoriert, wenn das Formular keine eigene Box darstellt
     * (weil es in einem modalen Dialog ist oder in eine Box eingebettet ist.)
     */
    @Input() infoBoxTitle: string;

    /**
     * Gibt an welchen Schritt von n Schritten das aktuelle Formular darstellt.
     */
    @Input() progressStep: number;

    /**
     * Gibt an wie viele Schritte es maximal gibt.
     */
    @Input() progressMaxSteps: number;

    /**
     * Die Namen aller Schritte ({@link #progressMaxSteps}).
     */
    @Input() progressStepNames: string[];

    /**
     * Formulare haben als Default eine half Box. Für eine full Box kann dieser Parameter auf true gesetzt werden.
     * @deprecated
     * Bitte nicht mehr nutzen, stattdessen: Wird eine fullBox benötigt, dann soll das Formular in eine entprechende Box
     * eingepackt werden:
     *
     * ```
     *  <div class="box full">
     *      <div class="innerBox">
     *          <common-form>...</common-form>
     *      </div>
     *  </div>
     * ```
     * Die Formularkomponente erkennt dabei automatisch, dass sie nicht noch selber eine Box darstellen soll.
     */
    @Input() boxSizeFull = false;

    /**
     * Falls gesetzt, wird keine Box gerendert. Weiterhin werden die Paramter für die infoBox ignoriert
     * @deprecated
     * Bitte nicht mehr nutzen, stattdessen soll das Formular in eine entprechende Box gewünschter Größe
     * (div mit class "box full" , "box half" usw.) eingepackt werden. Damit sind z.B. auch zwei Formulare nebeneinander möglich:
     *
     * ```
     *  <common-box size="half">
     *      <common-form>...</common-form>
     *  </common-box>
     *  <div class="box half">
     *      <div class="innerBox">
     *          <common-form>...</common-form>
     *      </div>
     *  </div>
     * ```
     *
     * Die Formularkomponente erkennt dabei automatisch, dass sie nicht noch selber eine Box darstellen soll.
     */
    @Input() noBox = false;

    @Input() formGroup: UntypedFormGroup;

    /**
     * Gibt an wo Buttons zum Speichern und abbrechen angezeigt werden sollen.
     */
    @Input() mirrorBtn: 'top' | 'bottom' | 'bottom top' | 'top bottom';

    /**
     * Gibt an wo Buttons zum Speichern und abbrechen angezeigt werden sollen.
     */
    @Input() mirrorIcons: 'top' | 'bottom' | 'bottom top' | 'top bottom';
    // Falls gesetzt wird Hinweis zu den Pflichtfeldern angezeigt.
    @Input() requiredFieldsHint: boolean = false;

    @ContentChild('icons')
    icons;

    @ContentChild('buttons')
    buttons;

    i18n: any;

    // wo sollen die Buttons und die Icons platziert werden?
    progressStepIndicator: boolean = false;
    placeBtnAtTop: boolean = false;
    placeBtnAtBottom: boolean = true;
    placeIconsAtTop: boolean = true;
    placeIconsAtBottom: boolean = false;

    private readonly languageSubscription: Subscription;

    constructor(@Optional() private modalDialogWrapper: ModalDialogWrapperComponent,
                @Optional() private surroundingBox: BoxDirective,
                @Optional() private surroundingBoxComponent: BoxComponent,
                private loggingService: LoggingService,
                private languageService: LanguageService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit(): void {
        if (this.progressStep && this.progressMaxSteps) {
            this.progressStepIndicator = true;
        }

        this.mirrorComponents();
    }

    get isInModalDialog() {
        return !!this.modalDialogWrapper;
    }

    // Eine Box wird nur dann angezeigt, wenn noBox nicht true ist und sich das Formular nicht in einem modalen Dialog befindet
    // und das Formular nicht bereits von einer Box umgeben wird.
    // TODO: Sobald alle Formulare (außer die mit Infobox) mit einer "externen" Box versehen wurden, sollten wir die Logik
    // wie folgt ändern (SPP-251?):
    // - Default für this.noBox ist undefined
    // - Sofern noBox explizit gesetzt wird, gilt der Wert (außer in modalen Dialogen)
    // - Wenn noBox nicht explizit gesetzt wird, wird die Box genau dann angezeigt, wenn ein infoBoxText oder infoBoxTitle vorhanden ist.
    get showBox() {
        return !this.noBox && !this.surroundingBox && !this.surroundingBoxComponent && !this.isInModalDialog;
    }

    get formGroupElement() {
        return this.formGroup;
    }

    get boxSize(): 'full' | 'half' {
        return this.boxSizeFull ? 'full' : 'half';
    }

    mirrorComponents() {
        if (this.mirrorBtn) {

            this.loggingService.debug('Auswahl Position gesetzt mit ' + this.mirrorBtn);

            switch (this.mirrorBtn) {
                case 'top':
                    this.placeBtnAtTop = true;
                    this.placeBtnAtBottom = false;
                    break;
                case 'bottom':
                    this.placeBtnAtTop = false;
                    this.placeBtnAtBottom = true;
                    break;
                case 'top bottom':
                case 'bottom top':
                    this.placeBtnAtTop = true;
                    this.placeBtnAtBottom = true;
                    break;
                default:
                    this.placeBtnAtTop = false;
                    this.placeBtnAtBottom = true;
                    break;
            }
        }

        if (this.mirrorIcons) {

            this.loggingService.debug('Auswahl Position gesetzt mit ' + this.mirrorIcons);

            switch (this.mirrorIcons) {
                case 'top':
                    this.placeIconsAtTop = true;
                    this.placeIconsAtBottom = false;
                    break;
                case 'bottom':
                    this.placeIconsAtTop = false;
                    this.placeIconsAtBottom = true;
                    break;
                case 'top bottom':
                case 'bottom top':
                    this.placeIconsAtTop = true;
                    this.placeIconsAtBottom = true;
                    break;
                default:
                    this.placeIconsAtTop = true;
                    this.placeIconsAtBottom = false;
                    break;
            }
        }

        this.loggingService.debug('Buttons ' + (this.placeBtnAtTop ? 'oben' : 'nicht oben') + ' platzieren\n' +
            'Buttons ' + (this.placeBtnAtBottom ? 'unten' : ' nicht unten') + ' platzieren\n' +
            'Icons ' + (this.placeIconsAtTop ? 'oben' : 'nicht oben') + ' platzieren\n' +
            'Icons ' + (this.placeIconsAtBottom ? 'unten' : 'nicht unten') + ' platzieren');
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }
}
